import { useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faFileExport,
  faFileMagnifyingGlass,
} from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { t as translate } from "i18next";
import { useAuth } from "react-oidc-context";
import { AnalyticsWidgetUiDetailsDto } from "../../../types/analytics";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import { GroupedJourneys } from "../../../types/analytics/FilterAvailableValues";
import IAnalyticsDataItem from "../../../types/analytics/IAnalyticsDataItem";
import { DataRowWithEmployeeDetails } from "../../../types/analytics/tables/DataRowWithEmployeeDetails";
import { KeyValuePair } from "../../../types/generic";
import analyticsCustomEndpointApi from "../../../api/analytics/analyticsCustomEndpointApi";
import { fileHelper } from "../../../helpers";
import TinyLoader from "../../loaders/TinyLoader";
import { AnalyticsDataTable } from "../tables/AnalyticsDataTable";
import UsageEmployeeNudgeCell from "./UsageEmployeeNudgeCell";
import UsageNudgeBatchComposeModal from "./UsageNudgeBatchComposeModal";
import UsageTableFilterChip from "./UsageTableFilterChip";
import { UsageTableFilterChipLink } from "../../../helpers/usage/usageTableFilterHelpers";
import { Link } from "react-router-dom";
import Toast from "../../common/Toast";
import { ToastConfig } from "../../common/ToastConfig";
import { Tooltip } from "../../common";
import { UsageNudgeBatchStatusDto } from "../../../api/analytics/usageNudgeApi";
import UserContext from "../../../state/UserContext";

interface UsageDataTabPanelProps {
  widget: AnalyticsWidgetUiDetailsDto;
  filters: AnalyticsFilterInputs;
  journeys: GroupedJourneys[];
  otherApiParams: KeyValuePair<string, string>[] | null;
  filterLabel?: string | null;
  filterStatusKey?: string | null;
  onClearFilter?: () => void;
  onNavigateToFocus?: (link: UsageTableFilterChipLink) => void;
  canSendNudges?: boolean;
}

interface UsageDataItem extends IAnalyticsDataItem {
  approvalStatus?: string | null;
  collabDocUrl?: string | null;
  canView?: boolean;
  canNudge?: boolean;
  isForceClosed?: boolean;
  lastEmployeeNudgeSentAt?: string | null;
  lastEmployeeNudgeSenderName?: string | null;
}

interface NudgeCellOverride {
  sentAt: string;
  senderName: string | null;
}

function UsageDataTabPanel({
  widget,
  filters,
  journeys,
  otherApiParams,
  filterLabel = null,
  filterStatusKey = null,
  onClearFilter,
  onNavigateToFocus,
  canSendNudges = false,
}: UsageDataTabPanelProps) {
  const { t } = useTranslation();
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const api = new analyticsCustomEndpointApi(auth.user?.access_token);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<Set<number>>(
    new Set(),
  );
  const [nudgeOverrides, setNudgeOverrides] = useState<
    Map<number, NudgeCellOverride>
  >(new Map());
  const [isBatchNudgeOpen, setIsBatchNudgeOpen] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [toastConfig, setToastConfig] = useState(ToastConfig.hidden());

  if (!widget.tableApiEndpoint) {
    return null;
  }

  const canExportExcel =
    Boolean(widget.excelApiEndpoint) && widget.excelApiEndpoint!.length > 0;
  const exportLabel = t("Common.Export", "Export");

  const getUsageData = (row: DataRowWithEmployeeDetails<IAnalyticsDataItem>) =>
    row.data as UsageDataItem;

  const isSelectable = (row: DataRowWithEmployeeDetails<IAnalyticsDataItem>) =>
    Boolean(getUsageData(row).canNudge);

  const markEmployeesSent = (employeeIds: number[]) => {
    const sentAt = new Date().toISOString();
    setNudgeOverrides((current) => {
      const next = new Map(current);
      employeeIds.forEach((id) =>
        next.set(id, {
          sentAt,
          senderName: userContext.user.fullName,
        }),
      );
      return next;
    });
    setSelectedEmployeeIds((current) => {
      const next = new Set(current);
      employeeIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const setNudgeOverride = (
    employeeId: number,
    override: NudgeCellOverride | null,
  ) => {
    setNudgeOverrides((current) => {
      const next = new Map(current);
      if (override) {
        next.set(employeeId, override);
      } else {
        next.delete(employeeId);
      }
      return next;
    });
  };

  const handleBatchCompleted = (status: UsageNudgeBatchStatusDto) => {
    const cooldownSkipped = status.recipients.filter(
      (recipient) =>
        recipient.outcome === "COOLDOWNACTIVE" ||
        recipient.outcome === "COOLDOWN-ACTIVE",
    ).length;

    setToastConfig(
      ToastConfig.success(
        t("Analytics.Widgets.Usage.Nudge.BatchOutcome", {
          defaultValue:
            "Nudged {{sent}}. Skipped {{skipped}} already nudged in the last 72 hours.",
          sent: status.sentCount,
          skipped: cooldownSkipped,
        }),
        "",
      ),
    );
  };

  const downloadExcelFile = () => {
    if (!widget.excelApiEndpoint) return;

    setIsDownloadingExcel(true);
    const filenameWithoutExtension = fileHelper.createFilenameFromString(
      translate(widget.widgetTitle),
      true,
    );

    api.downloadSpreadsheetFile(
      widget.excelApiEndpoint,
      filenameWithoutExtension,
      filters,
      () => setIsDownloadingExcel(false),
      (error) => {
        setIsDownloadingExcel(false);
        console.error("Unable to download file", error);
      },
    );
  };

  return (
    <section
      className="mx-3 mt-6 border-t border-gray-200 pt-6"
      aria-labelledby="usage-data-heading"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2
            id="usage-data-heading"
            className="font-heading text-xl font-semibold text-gray-700"
          >
            {t("Analytics.Widgets.Usage.DataTab.Heading", "Assigned journeys")}
          </h2>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            {t(
              "Analytics.Widgets.Usage.DataTab.Description",
              "Search and browse individual journey assignments for the current filter selection",
            )}
          </p>
        </div>
        {canExportExcel && (
          <button
            type="button"
            className="btn-secondary btn-small inline-flex shrink-0 items-center gap-1.5"
            onClick={downloadExcelFile}
            disabled={isDownloadingExcel}
          >
            {isDownloadingExcel ? (
              <TinyLoader size="sm" classNames="primary-colour" />
            ) : (
              <FontAwesomeIcon icon={faFileExport} aria-hidden />
            )}
            {exportLabel}
          </button>
        )}
      </div>
      <div className="mt-4">
        {filterLabel && onClearFilter && onNavigateToFocus && (
          <UsageTableFilterChip
            label={filterLabel}
            statusKey={filterStatusKey}
            onClear={onClearFilter}
            onNavigateToFocus={onNavigateToFocus}
          />
        )}
        <AnalyticsDataTable
          apiUrl={widget.tableApiEndpoint}
          filters={filters}
          journeys={journeys}
          legendItems={widget.legendItems}
          otherApiParams={otherApiParams}
          skeletonColumnCount={8}
          bulkActions={
            canSendNudges && selectedEmployeeIds.size > 0 ? (
              <button
                type="button"
                className="btn-secondary btn-small inline-flex items-center gap-1.5"
                onClick={() => setIsBatchNudgeOpen(true)}
              >
                <FontAwesomeIcon icon={faBell} aria-hidden />
                {t("Analytics.Widgets.Usage.Nudge.Selected", {
                  defaultValue: "Nudge selected ({{count}})",
                  count: selectedEmployeeIds.size,
                })}
              </button>
            ) : undefined
          }
          rowSelection={
            canSendNudges
              ? {
                  selectedEmployeeIds,
                  canSelect: isSelectable,
                  onToggle: (employeeId) =>
                    setSelectedEmployeeIds((current) => {
                      const next = new Set(current);
                      if (next.has(employeeId)) {
                        next.delete(employeeId);
                      } else {
                        next.add(employeeId);
                      }
                      return next;
                    }),
                  onSelectPage: (employeeIds, selected) =>
                    setSelectedEmployeeIds((current) => {
                      const next = new Set(current);
                      employeeIds.forEach((id) =>
                        selected ? next.add(id) : next.delete(id),
                      );
                      return next;
                    }),
                }
              : undefined
          }
          rowAction={{
            header: t("Common.Actions", "Actions"),
            hideWhenNoViewPermission: true,
            forceVisible: canSendNudges,
            render: (row) => {
              const data = getUsageData(row);
              const canViewDoc = Boolean(data.canView && data.collabDocUrl);
              const showNudge = Boolean(data.canNudge && canSendNudges);

              if (!canViewDoc && !showNudge) {
                return (
                  <span
                    className="text-gray-400"
                    aria-label={t("Common.NoAction", "No action")}
                  >
                    -
                  </span>
                );
              }

              const viewLabel = t("Common.View", "View");
              const viewIconOnly = showNudge;
              const override = nudgeOverrides.get(row.employeeId);

              const viewLink = canViewDoc ? (
                <Link
                  to={data.collabDocUrl!}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={viewLabel}
                  className={
                    viewIconOnly
                      ? "btn-secondary btn-small inline-flex items-center !px-2 !text-[13px]"
                      : "btn-secondary btn-small inline-flex items-center gap-1.5 !text-[13px]"
                  }
                >
                  <FontAwesomeIcon icon={faFileMagnifyingGlass} aria-hidden />
                  {!viewIconOnly && viewLabel}
                </Link>
              ) : null;

              return (
                <div className="flex items-center justify-center gap-2">
                  {viewLink &&
                    (viewIconOnly ? (
                      <Tooltip content={viewLabel} triggerElement={viewLink} />
                    ) : (
                      viewLink
                    ))}
                  {showNudge && (
                    <UsageEmployeeNudgeCell
                      employeeId={row.employeeId}
                      employeeName={row.fullName}
                      filters={filters}
                      lastSentAt={
                        override?.sentAt ?? data.lastEmployeeNudgeSentAt
                      }
                      lastSenderName={
                        override?.senderName ?? data.lastEmployeeNudgeSenderName
                      }
                      onSendStarted={() =>
                        setNudgeOverride(row.employeeId, {
                          sentAt: new Date().toISOString(),
                          senderName: userContext.user.fullName,
                        })
                      }
                      onSendFailed={() =>
                        setNudgeOverride(row.employeeId, null)
                      }
                      onCooldown={(lastSentAt, senderName) =>
                        setNudgeOverride(row.employeeId, {
                          sentAt: lastSentAt,
                          senderName,
                        })
                      }
                      onSent={() => markEmployeesSent([row.employeeId])}
                    />
                  )}
                </div>
              );
            },
          }}
        />
      </div>
      <UsageNudgeBatchComposeModal
        isOpen={isBatchNudgeOpen}
        onOpenChange={setIsBatchNudgeOpen}
        filters={filters}
        recipientEmployeeIds={Array.from(selectedEmployeeIds)}
        audience="EMPLOYEE"
        onSent={markEmployeesSent}
        onCompleted={handleBatchCompleted}
      />
      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </section>
  );
}

export default UsageDataTabPanel;
